"use client";

import { useEffect, useState } from "react";
import { useLocalizedPathname as usePathname, useMessages } from "@/lib/i18n/client";
import messages from "./OrganizerCtaModal.i18n";
import Link from "@/components/i18n/link";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { fetchOrganizations } from "@/lib/api";
import { useAuthStore } from "@/lib/store/auth-store";

const STORAGE_KEY = "cv-organizer-cta-seen";

// El layout raíz monta este componente una sola vez por carga de página, así
// que este efecto solo corre en la primera entrada al sitio (cualquier
// página) y no se vuelve a disparar al navegar entre rutas (Next no
// remonta el layout en navegación client-side). sessionStorage evita que
// reaparezca si el usuario recarga la misma pestaña.
//
// `user`/`checked` vienen de useAuthStore (hidratado por AuthHydrator con un
// fetch client-side a /auth/me) en vez de una prop server-side — así el
// layout raíz no depende de cookies() y las páginas sin datos propios pueden
// pre-generarse como estáticas. Se espera a `checked` antes de decidir si
// mostrar el modal, para no invitar a alguien ya logeado a "loguearse para
// publicar" solo porque el fetch de sesión todavía no resolvía.
export function OrganizerCtaModal() {
  const pathname = usePathname();
  const user = useAuthStore((s) => s.user);
  const checked = useAuthStore((s) => s.checked);
  const [open, setOpen] = useState(false);
  const [orgCount, setOrgCount] = useState<number | null>(null);
  const t = useMessages(messages);

  useEffect(() => {
    if (!checked) return;
    if (user) return;
    // /aviso: llega desde el correo de "Avísame" a ver su confirmación.
    if (pathname === "/login" || pathname === "/registro" || pathname === "/aviso") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;

    sessionStorage.setItem(STORAGE_KEY, "1");
    // sessionStorage y la sesión solo se conocen en el navegador.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(true);
    fetchOrganizations()
      .then((data) => setOrgCount(data.length))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">{t.eyebrow}</p>
          <DialogTitle className="mt-1 text-xl">{t.title}</DialogTitle>
          <DialogDescription className="mt-2">
            {t.body}
            {orgCount !== null && orgCount > 0 && (
              <>
                {t.countPre}<span className="font-medium text-foreground">{t.count(orgCount)}</span>{t.countPost}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <Link
          href="/registro"
          onClick={() => setOpen(false)}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
        >
          {t.cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </DialogContent>
    </Dialog>
  );
}
